import type { ProjectId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import {
  apartmentMeasureUnit,
  apartmentReferenceKey,
  ownQuantity,
  type ApartmentAverage,
  type ApartmentItem,
  type ApartmentOutOfAverage,
  type ApartmentSteelRate,
} from './apartment-average';
import { serviceElement, type AppliesTo } from './applies-to';
import type { GroundBeam, PileCap } from './foundation';
import {
  foundationPieces,
  foundationPieceSteel,
  type FoundationElement,
  type FoundationFactors,
  type FoundationPieceMeasure,
} from './foundation-factors';
import type { MeasureBy } from './geometry';
import { importedPieceKind } from './imported-piece';
import { ownFactorKey, type ItemFactor } from './own-factor';
import type { PileCapType } from './pile-cap-type';
import { nomeDaPeca } from './planilha-por-peca';
import { serviceLockKey, serviceLockKeyOf } from './project-budget';
import { referenceItemFor, type ReferenceItem } from './rate-average';
import type { Slab } from './slab';
import { STEEL_GAUGES, steelRowsFor, type SteelGauge, type SteelRate } from './steel';

/**
 * A média da fundação: bloco, baldrame e radier (Fase 41, etapa 3 — F4, F5, F6; RN-548 a RN-552;
 * ADR-186).
 *
 * Uma média só por serviço × item, para todas as peças (F5), de duas fontes (F4):
 *
 * - **a peça desenhada:** a medida dela na planta × o fator que vale nela (o próprio do card, o do
 *   tipo de bloco, ou o índice da obra) × (1 + perda). O índice que veio da média não é número de
 *   ninguém (D5);
 * - **a peça da planilha por peça:** a quantidade do item ÷ a quantidade do serviço na mesma peça.
 *   A peça sem a quantidade do serviço não entra naquele serviço, e a aba diz quantas ficaram fora
 *   (RN-555).
 *
 * A medida da peça é conta do servidor (ADR-186): o contorno, a altura, a seção e a espessura estão
 * gravados, e as funções são as mesmas da tela (`foundationPieces`, `foundationPieceSteel`). Não há
 * foto, ao contrário do apartamento (ADR-182).
 *
 * O aço vai à parte, por elemento e bitola (RN-552), como o da laje: Σ (taxa × volume) ÷ Σ volume;
 * na peça importada, a taxa é o kg da bitola ÷ o m³ da CONCRETAGEM dela (RN-557).
 *
 * Desde a Fase 42 (etapa 4 — G11, G13; RN-582; ADR-191):
 *
 * - **a linha "da média" do tipo de bloco não é número de ninguém (D5):** o tipo que nasceu com a
 *   média leva o índice médio e a taxa média marcados `MEDIA`. O bloco cujo fator que vale num
 *   item veio de uma linha dessas fica fora daquele item, e a bitola `MEDIA` da taxa que vale nele
 *   fica fora do aço — como o índice da obra que veio da média;
 * - **o arranque é da estrutura:** a peça importada cujo tipo é `ARRANQUE` (`importedPieceKind`,
 *   pelas disciplinas da peça) sai daqui, mesmo com um serviço que se aplica ao bloco — na Veneza,
 *   a fôrma do arranque aparecia como "fora". Ela entra na média da estrutura (G13).
 *
 * Função pura sobre o que o repositório leu.
 */

const DA_FUNDACAO: ReadonlySet<string> = new Set<FoundationElement>([
  'BLOCO',
  'BALDRAME',
  'RADIER',
]);

/** O elemento da fundação que o serviço mede, ou `undefined`. */
export function foundationElementOf(service: {
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
}): FoundationElement | undefined {
  const elemento = serviceElement(service);
  return elemento !== undefined && DA_FUNDACAO.has(elemento)
    ? (elemento as FoundationElement)
    : undefined;
}

/** O desenho da fundação de uma obra marcada: as peças, os tipos de bloco e os nomes importados. */
export interface FoundationDrawing {
  readonly projectId: ProjectId;
  readonly pileCaps: readonly PileCap[];
  readonly groundBeams: readonly GroundBeam[];
  readonly slabs: readonly Slab[];
  readonly pileCapTypes: readonly PileCapType[];
}

/** Uma linha da planilha por peça de um serviço da fundação (`piece_quantities`). */
export interface FoundationPieceLine {
  readonly projectId: ProjectId;
  readonly unitTypeId: string;
  readonly pieceName: string;
  /** A chave do catálogo do serviço. */
  readonly serviceKey: string;
  /** Os nomes do serviço, para dizer na aba o que ficou fora (RN-555). */
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  /** O item; ausente é a quantidade do **serviço** na peça. */
  readonly itemId?: string;
  readonly steelGauge?: SteelGauge;
  readonly quantity: number;
}

export interface FoundationInput {
  readonly drawings: readonly FoundationDrawing[];
  /** Os itens vivos dos serviços das obras marcadas (os mesmos do apartamento). */
  readonly items: readonly ApartmentItem[];
  /** A taxa de aço das disciplinas das obras marcadas, com a origem. */
  readonly steelRates: readonly ApartmentSteelRate[];
  readonly pieceLines: readonly FoundationPieceLine[];
}

/** A média da taxa de uma bitola no aço de um elemento da fundação (RN-552). */
export interface FoundationSteelAverage {
  readonly element: FoundationElement;
  readonly gauge: SteelGauge;
  /** kg/m³. */
  readonly rate: number;
  readonly volume: number;
  readonly projects: number;
  /** Quantas peças usam a bitola (RN-555), como `projects`. Ausente: o servidor de antes. */
  readonly pieces?: number;
}

/** Por que uma peça da planilha por peça ficou fora de um serviço da média da fundação. */
export type FoundationPieceOutReason =
  /** A planilha não traz a quantidade do serviço na peça: o item não tem por onde dividir (RN-550). */
  | 'SEM_QUANTIDADE_DO_SERVICO'
  /** A peça não tem a CONCRETAGEM: o kg das bitolas não tem o m³ por onde dividir (RN-557). */
  | 'SEM_CONCRETAGEM';

export const FOUNDATION_PIECE_OUT_LABEL: Readonly<Record<FoundationPieceOutReason, string>> = {
  SEM_QUANTIDADE_DO_SERVICO: 'peça da planilha sem a quantidade do serviço',
  SEM_CONCRETAGEM: 'peça da planilha sem a concretagem, fora do aço',
};

/**
 * As peças da planilha por peça que ficaram fora de um serviço (RN-555), uma vez por motivo ×
 * serviço: quantas, e de quantas obras. No aço, o serviço é o que traz o kg das bitolas.
 */
export interface FoundationPiecesOut {
  readonly reason: FoundationPieceOutReason;
  /** A chave da trava do serviço (`serviceLockKey`), como a das médias. */
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly pieces: number;
  readonly projects: number;
}

export interface FoundationAverages {
  /** Por serviço × item; `unitTypes` aqui é quantas peças entraram. */
  readonly averages: readonly ApartmentAverage[];
  readonly outOfAverage: readonly ApartmentOutOfAverage[];
  readonly steel: readonly FoundationSteelAverage[];
  /** Quantas peças (desenhadas e importadas) as obras marcadas têm — zero é "sem fundação". */
  readonly pieces: number;
  /**
   * As peças da planilha que ficaram fora por falta da medida, por motivo × serviço (RN-555).
   * Ausente: o servidor de antes.
   */
  readonly piecesOut?: readonly FoundationPiecesOut[];
}

export const NO_FOUNDATION_AVERAGES: FoundationAverages = {
  averages: [],
  outOfAverage: [],
  steel: [],
  pieces: 0,
  piecesOut: [],
};

interface Acumulado {
  readonly serviceKey: string;
  readonly item: ReferenceItem;
  readonly unit: string;
  readonly ordem: number;
  quantity: number;
  measure: number;
  readonly projects: Set<string>;
  readonly pieces: Set<string>;
}

/** Um número próprio de um item: a quantidade e a medida de uma peça. */
interface Numero {
  readonly projectId: ProjectId;
  readonly piece: string;
  readonly quantity: number;
  readonly measure: number;
}

/** As peças da planilha que ficaram fora de um serviço, por motivo (RN-555). */
interface Fora {
  readonly reason: FoundationPieceOutReason;
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly pieces: Set<string>;
  readonly projects: Set<string>;
}

/** As peças desenhadas de uma obra, como a média as lê, e o que nelas veio da média (D5). */
interface Desenhadas extends FoundationFactors<FoundationElement> {
  /** Pelo id do bloco, as chaves (`ownFactorKey`) cujo fator que vale nele é "da média". */
  readonly daMedia: ReadonlyMap<string, ReadonlySet<string>>;
}

/**
 * As linhas "da média" que valem em cada bloco (D5; Fase 42, etapa 4 — RN-577, RN-582, ADR-191): a
 * composição do tipo de bloco vivo com o próprio do card por cima, como na conta
 * (`pileCapFactors`); a chave cuja linha que vale tem a origem `MEDIA` deixa o bloco fora daquele
 * item. O número digitado no tipo, ou no card, entra.
 */
function daMediaNosBlocos(
  caps: readonly PileCap[],
  types: readonly PileCapType[],
): ReadonlyMap<string, ReadonlySet<string>> {
  const tipos = new Map(types.map((tipo) => [String(tipo.id), tipo]));
  const porBloco = new Map<string, ReadonlySet<string>>();
  for (const cap of caps) {
    if (cap.deletedAt !== undefined) continue;
    const tipo = cap.pileCapTypeId === undefined ? undefined : tipos.get(cap.pileCapTypeId);
    const doTipo = tipo === undefined || tipo.deletedAt !== undefined ? [] : tipo.rates;
    const valem = new Map<string, ItemFactor>();
    for (const linha of [...doTipo, ...(cap.factors ?? [])]) {
      valem.set(ownFactorKey(linha.serviceKey, linha.itemKey), linha);
    }
    const daMedia = new Set(
      [...valem].filter(([, linha]) => linha.origin === 'MEDIA').map(([chave]) => chave),
    );
    if (daMedia.size > 0) porBloco.set(cap.id, daMedia);
  }
  return porBloco;
}

/**
 * As peças da planilha por peça que são arranque (Fase 42, G13): pelo tipo da peça, que sai das
 * disciplinas de todas as linhas dela (`importedPieceKind`), e não pelo "aplica-se" do serviço.
 */
export function pecasDoArranque(linhas: readonly FoundationPieceLine[]): ReadonlySet<string> {
  const disciplinas = new Map<string, string[]>();
  for (const linha of linhas) {
    const peca = PECA_DA_LINHA(linha);
    disciplinas.set(peca, [...(disciplinas.get(peca) ?? []), linha.disciplineName]);
  }
  return new Set(
    [...disciplinas]
      .filter(([, nomes]) => importedPieceKind(nomes) === 'ARRANQUE')
      .map(([peca]) => peca),
  );
}

/**
 * A taxa de cada bitola numa lista, para a média do aço (D5; Fase 42, etapa 4 — ADR-191): a bitola
 * "da média" fica fora daquela peça (`undefined`), a que a lista não tem entra com zero. Sem
 * nenhuma bitola digitada, a peça toda fica fora do aço (`undefined`). Vale para a taxa da
 * disciplina, a do tipo e a própria — a própria não tem origem, e entra inteira.
 */
export function ownSteelRatesForAverage(
  lista: readonly SteelRate[],
): ((gauge: SteelGauge) => number | undefined) | undefined {
  const digitadas = lista.filter((taxa) => taxa.origin !== 'MEDIA' && taxa.kgPerM3 > 0);
  if (digitadas.length === 0) return undefined;
  const daMedia = new Set(
    lista.filter((taxa) => taxa.origin === 'MEDIA').map((taxa) => taxa.gauge),
  );
  const valor = new Map(digitadas.map((taxa) => [taxa.gauge, taxa.kgPerM3]));
  return (gauge) => (daMedia.has(gauge) ? undefined : (valor.get(gauge) ?? 0));
}

/** Anota a peça (`obra|tipologia|nome`) fora do serviço da linha. */
type AnotarFora = (
  reason: FoundationPieceOutReason,
  linha: FoundationPieceLine,
  peca: string,
) => void;

const DA_DISCIPLINA = (projectId: ProjectId, disciplina: string) =>
  `${projectId}|${normalizeName(disciplina)}`;

/** A peça da planilha por peça: a mesma obra, tipologia e nome (`nomeDaPeca`). */
const PECA_DA_LINHA = (linha: FoundationPieceLine) =>
  `${linha.projectId}|${linha.unitTypeId}|${nomeDaPeca(linha.pieceName)}`;

/**
 * O m³ de concreto da peça importada é o da CONCRETAGEM dela (RN-557), pelo nome do serviço, como
 * o resto do catálogo compara — e sem o sufixo da versão (`serviceLockKey`).
 */
const DA_CONCRETAGEM = `|${normalizeName('CONCRETAGEM')}`;
const ehConcretagem = (linha: FoundationPieceLine): boolean =>
  serviceLockKey(linha.serviceKey).endsWith(DA_CONCRETAGEM);

/**
 * A média da fundação das obras marcadas (RN-549 a RN-552), com o que ficou fora (RN-555). Sem
 * referência, nada (D3).
 */
export function averageFoundationRates(
  input: FoundationInput,
  reference: readonly ReferenceItem[] | undefined,
): FoundationAverages {
  if (reference === undefined) return NO_FOUNDATION_AVERAGES;

  // A peça da planilha por peça vale pelo número dela (F10): a desenhada de mesmo nome sai.
  const importadas = new Set(input.pieceLines.map(PECA_DA_LINHA));
  // O arranque é da estrutura (Fase 42, G13): as linhas dele saem daqui, seja qual for o
  // "aplica-se" do serviço. Ele continua tirando a desenhada de mesmo nome (`importadas`).
  const arranques = pecasDoArranque(input.pieceLines);
  const linhas = input.pieceLines.filter((linha) => !arranques.has(PECA_DA_LINHA(linha)));
  const desenhadas = new Map<ProjectId, Desenhadas>();
  let pecas = 0;
  for (const desenho of input.drawings) {
    const fora = <P extends { readonly unitTypeId: string; readonly name: string }>(
      lista: readonly P[],
    ) =>
      lista.filter(
        (peca) =>
          !importadas.has(`${desenho.projectId}|${peca.unitTypeId}|${nomeDaPeca(peca.name)}`),
      );
    const blocos = fora(desenho.pileCaps);
    const base = foundationPieces({
      pileCaps: blocos,
      groundBeams: fora(desenho.groundBeams),
      slabs: fora(desenho.slabs),
      pileCapTypes: desenho.pileCapTypes,
    });
    pecas += base.pieces.length;
    desenhadas.set(desenho.projectId, {
      ...base,
      daMedia: daMediaNosBlocos(blocos, desenho.pileCapTypes),
    });
  }
  pecas += new Set(
    linhas.filter((linha) => foundationElementOf(linha) !== undefined).map(PECA_DA_LINHA),
  ).size;

  // O que ficou fora por falta da medida na planilha (RN-555): por motivo × serviço, as peças.
  const foraDasPecas = new Map<string, Fora>();
  const anotarFora: AnotarFora = (reason, linha, peca) => {
    const servico = serviceLockKey(linha.serviceKey);
    const chave = `${reason}|${servico}`;
    const anotado = foraDasPecas.get(chave) ?? {
      reason,
      serviceKey: servico,
      disciplineName: linha.disciplineName,
      serviceName: linha.serviceName,
      pieces: new Set<string>(),
      projects: new Set<string>(),
    };
    anotado.pieces.add(peca);
    anotado.projects.add(linha.projectId);
    foraDasPecas.set(chave, anotado);
  };

  // A taxa da disciplina de cada obra, para o aço medido da peça sem taxa própria.
  const taxasDaDisciplina = new Map<string, ApartmentSteelRate[]>();
  for (const taxa of input.steelRates) {
    const chave = `${taxa.projectId}|${taxa.disciplineKey}`;
    taxasDaDisciplina.set(chave, [...(taxasDaDisciplina.get(chave) ?? []), taxa]);
  }

  // As linhas por peça: a quantidade do serviço e a de cada item, por obra × tipologia × peça.
  const doServico = new Map<string, number>();
  const doItem = new Map<string, FoundationPieceLine[]>();
  for (const linha of linhas) {
    if (foundationElementOf(linha) === undefined || !(linha.quantity > 0)) continue;
    const peca = PECA_DA_LINHA(linha);
    if (linha.itemId === undefined) {
      doServico.set(`${peca}|${linha.serviceKey}`, linha.quantity);
    } else {
      doItem.set(linha.itemId, [...(doItem.get(linha.itemId) ?? []), linha]);
    }
  }

  const daReferencia = new Map<string, ReferenceItem[]>();
  reference.forEach((item) => {
    const chave = serviceLockKeyOf(item.disciplineName, item.serviceName);
    daReferencia.set(chave, [...(daReferencia.get(chave) ?? []), item]);
  });
  const ordemDaReferencia = new Map(reference.map((item, indice) => [item, indice]));

  const acumulados = new Map<string, Acumulado>();
  const fora = new Map<string, ApartmentOutOfAverage>();

  for (const item of input.items) {
    const elemento = foundationElementOf(item);
    if (elemento === undefined || item.steelGauge !== undefined) continue;
    const numeros: Numero[] = [];

    // A peça desenhada (RN-549).
    const base = desenhadas.get(item.projectId);
    const chave = ownFactorKey(item.serviceKey, normalizeName(item.description));
    // A lista do elemento, na disciplina que mistura (Fase 42, RN-573).
    const disciplina = steelRowsFor(
      taxasDaDisciplina.get(DA_DISCIPLINA(item.projectId, item.disciplineName)) ?? [],
      elemento,
    );
    for (const peca of base?.pieces ?? []) {
      if (peca.element !== elemento) continue;
      // O fator que vale no bloco veio da linha "da média" do tipo: não é de ninguém (D5).
      if (base?.daMedia.get(peca.pieceId)?.has(chave) === true) continue;
      const medida = medidaDaPeca(peca, item.measureBy, disciplina);
      if (medida === undefined || !(medida > 0)) continue;
      const fator =
        base?.factors[peca.pieceId]?.[chave] ??
        (item.indexOrigin === 'MEDIA' ? undefined : item.consumptionIndex);
      if (fator === undefined) continue;
      const quantidade = ownQuantity(
        {
          kind: item.kind,
          consumptionIndex: fator,
          ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
        },
        medida,
        undefined,
      );
      if (quantidade === undefined) continue;
      numeros.push({
        projectId: item.projectId,
        piece: peca.pieceId,
        quantity: quantidade,
        measure: medida,
      });
    }

    // A peça da planilha por peça (RN-550). Sem a quantidade do serviço, ela não tem por onde
    // dividir: fica fora do serviço, e a aba diz quantas (RN-555).
    for (const linha of doItem.get(item.id) ?? []) {
      const peca = PECA_DA_LINHA(linha);
      const medida = doServico.get(`${peca}|${linha.serviceKey}`);
      if (medida === undefined || !(medida > 0)) {
        anotarFora('SEM_QUANTIDADE_DO_SERVICO', linha, peca);
        continue;
      }
      numeros.push({
        projectId: item.projectId,
        piece: peca,
        quantity: linha.quantity,
        measure: medida,
      });
    }
    if (numeros.length === 0) continue;

    const servico = serviceLockKey(item.serviceKey);
    const par = referenceItemFor(item, daReferencia.get(servico) ?? []);
    if (par === undefined) {
      const chaveFora = `${item.projectId}|${servico}|${normalizeName(item.description)}`;
      const antes = fora.get(chaveFora);
      fora.set(chaveFora, {
        reason: 'ITEM_SEM_PAR',
        projectId: item.projectId,
        serviceName: item.serviceName,
        description: item.description,
        unitTypes: (antes?.unitTypes ?? 0) + numeros.length,
      });
      continue;
    }
    const chaveDaMedia = `${servico}|${apartmentReferenceKey(par)}`;
    const acumulado = acumulados.get(chaveDaMedia) ?? {
      serviceKey: servico,
      item: par,
      unit: apartmentMeasureUnit({
        measureBy: item.measureBy,
        ...(item.appliesTo === undefined ? {} : { appliesTo: item.appliesTo }),
        ...(item.serviceUnit === undefined ? {} : { unit: item.serviceUnit }),
      }),
      ordem: ordemDaReferencia.get(par) ?? 0,
      quantity: 0,
      measure: 0,
      projects: new Set<string>(),
      pieces: new Set<string>(),
    };
    for (const numero of numeros) {
      acumulado.quantity += numero.quantity;
      acumulado.measure += numero.measure;
      acumulado.projects.add(numero.projectId);
      acumulado.pieces.add(`${numero.projectId}|${numero.piece}`);
    }
    acumulados.set(chaveDaMedia, acumulado);
  }

  const averages = [...acumulados.values()]
    .sort((a, b) => a.ordem - b.ordem)
    .map((a): ApartmentAverage => ({
      serviceKey: a.serviceKey,
      disciplineName: a.item.disciplineName,
      serviceName: a.item.serviceName,
      item: a.item,
      measureUnit: a.unit,
      rate: a.quantity / a.measure,
      quantity: a.quantity,
      measure: a.measure,
      projects: a.projects.size,
      unitTypes: a.pieces.size,
    }));

  // O aço anota antes a peça sem a concretagem, e o "fora" sai com os dois motivos.
  const steel = mediaDoAco(
    { ...input, pieceLines: linhas },
    desenhadas,
    taxasDaDisciplina,
    anotarFora,
  );
  return {
    averages,
    outOfAverage: [...fora.values()],
    steel,
    pieces: pecas,
    piecesOut: [...foraDasPecas.values()].map((anotado): FoundationPiecesOut => ({
      reason: anotado.reason,
      serviceKey: anotado.serviceKey,
      disciplineName: anotado.disciplineName,
      serviceName: anotado.serviceName,
      pieces: anotado.pieces.size,
      projects: anotado.projects.size,
    })),
  };
}

/**
 * A medida de uma peça na grandeza do serviço. No serviço de aço, os kg da peça: o volume × a soma
 * das bitolas da taxa que vale nela (a própria ou a do tipo; sem elas, a da disciplina) — a mesma
 * conta da parte da peça na tela (`foundationPieceSteel`, RN-556).
 */
function medidaDaPeca(
  peca: FoundationPieceMeasure,
  grandeza: MeasureBy,
  disciplina: readonly SteelRate[] | undefined,
): number | undefined {
  if (grandeza !== 'ACO') return peca.measures[grandeza];
  const kg = foundationPieceSteel(peca, disciplina);
  return kg > 0 ? kg : undefined;
}

/**
 * A média do aço por elemento e bitola (RN-552): Σ (taxa × volume) ÷ Σ volume. A bitola que a peça
 * não usa entra com zero; a da disciplina que veio da média fica fora daquela obra (D5). Na peça
 * importada, a taxa é o kg da bitola ÷ o m³ da CONCRETAGEM da mesma peça (RN-557); sem ela, a peça
 * fica fora do aço (RN-555). Cada bitola diz de quantas obras e peças veio.
 */
function mediaDoAco(
  input: FoundationInput,
  desenhadas: ReadonlyMap<ProjectId, Desenhadas>,
  taxasDaDisciplina: ReadonlyMap<string, readonly ApartmentSteelRate[]>,
  anotarFora: AnotarFora,
): readonly FoundationSteelAverage[] {
  const acumulado = new Map<
    string,
    { kg: number; volume: number; projects: Set<string>; pieces: Set<string> }
  >();
  const somar = (
    elemento: FoundationElement,
    projectId: ProjectId,
    peca: string,
    volume: number,
    taxa: (gauge: SteelGauge) => number | undefined,
  ) => {
    for (const { code } of STEEL_GAUGES) {
      const kg = taxa(code);
      if (kg === undefined) continue;
      const chave = `${elemento}|${code}`;
      const linha = acumulado.get(chave) ?? {
        kg: 0,
        volume: 0,
        projects: new Set<string>(),
        pieces: new Set<string>(),
      };
      linha.kg += kg * volume;
      linha.volume += volume;
      if (kg > 0) {
        linha.projects.add(projectId);
        linha.pieces.add(peca);
      }
      acumulado.set(chave, linha);
    }
  };

  // A disciplina do aço de cada elemento, em cada obra: a dos serviços que o medem — a do serviço
  // de aço, quando há item nele; senão, a de qualquer serviço do elemento.
  const disciplinaDoAco = new Map<string, string>();
  for (const item of input.items) {
    const elemento = foundationElementOf(item);
    if (elemento === undefined) continue;
    const chave = `${item.projectId}|${elemento}`;
    if (item.measureBy === 'ACO' || !disciplinaDoAco.has(chave)) {
      disciplinaDoAco.set(chave, normalizeName(item.disciplineName));
    }
  }

  // A peça desenhada: a taxa que vale nela — a própria, a do tipo de bloco ou a da disciplina —,
  // sem a bitola "da média" (D5; a do tipo, desde a Fase 42, ADR-191).
  for (const [projectId, base] of desenhadas) {
    for (const peca of base.pieces) {
      const volume = peca.measures.VOLUME ?? 0;
      if (!(volume > 0)) continue;
      const disciplina = disciplinaDoAco.get(`${projectId}|${peca.element}`);
      // A lista do elemento da peça, na disciplina que mistura (Fase 42, RN-573).
      const taxas =
        peca.steelRates ??
        steelRowsFor(
          disciplina === undefined
            ? []
            : (taxasDaDisciplina.get(`${projectId}|${disciplina}`) ?? []),
          peca.element,
        );
      const taxa = ownSteelRatesForAverage(taxas);
      if (taxa === undefined) continue;
      somar(peca.element, projectId, `${projectId}|${peca.pieceId}`, volume, taxa);
    }
  }

  // A peça da planilha por peça: o m³ da CONCRETAGEM (RN-557) e o aço por bitola. O maior número
  // protege da linha repetida, como na estaca. O aço é o do serviço que mais pesa: a planilha que
  // o repetisse na montagem o contaria duas vezes (`pieceMeasures`, RN-366).
  const porPeca = new Map<
    string,
    {
      projectId: ProjectId;
      peca: string;
      elemento: FoundationElement;
      volume: number;
      kgPorServico: Map<string, Map<SteelGauge, number>>;
      doAco: FoundationPieceLine[];
    }
  >();
  for (const linha of input.pieceLines) {
    const elemento = foundationElementOf(linha);
    if (elemento === undefined || !(linha.quantity > 0)) continue;
    const peca = PECA_DA_LINHA(linha);
    const daPeca = porPeca.get(`${peca}|${elemento}`) ?? {
      projectId: linha.projectId,
      peca,
      elemento,
      volume: 0,
      kgPorServico: new Map<string, Map<SteelGauge, number>>(),
      doAco: [],
    };
    if (linha.itemId === undefined && ehConcretagem(linha)) {
      daPeca.volume = Math.max(daPeca.volume, linha.quantity);
    } else if (linha.itemId !== undefined && linha.steelGauge !== undefined) {
      const doServico = daPeca.kgPorServico.get(linha.serviceKey) ?? new Map<SteelGauge, number>();
      doServico.set(linha.steelGauge, (doServico.get(linha.steelGauge) ?? 0) + linha.quantity);
      daPeca.kgPorServico.set(linha.serviceKey, doServico);
      daPeca.doAco.push(linha);
    }
    porPeca.set(`${peca}|${elemento}`, daPeca);
  }
  const somaDe = (kg: ReadonlyMap<SteelGauge, number>): number =>
    [...kg.values()].reduce((soma, valor) => soma + valor, 0);
  for (const daPeca of porPeca.values()) {
    const kg = [...daPeca.kgPorServico.values()].reduce<Map<SteelGauge, number> | undefined>(
      (maior, doServico) =>
        maior === undefined || somaDe(doServico) > somaDe(maior) ? doServico : maior,
      undefined,
    );
    if (kg === undefined) continue;
    if (!(daPeca.volume > 0)) {
      daPeca.doAco.forEach((linha) => anotarFora('SEM_CONCRETAGEM', linha, daPeca.peca));
      continue;
    }
    somar(
      daPeca.elemento,
      daPeca.projectId,
      daPeca.peca,
      daPeca.volume,
      (gauge) => (kg.get(gauge) ?? 0) / daPeca.volume,
    );
  }

  return [...acumulado.entries()].flatMap(([chave, linha]): FoundationSteelAverage[] => {
    if (!(linha.kg > 0) || !(linha.volume > 0)) return [];
    const [element, gauge] = chave.split('|') as [FoundationElement, SteelGauge];
    return [
      {
        element,
        gauge,
        rate: linha.kg / linha.volume,
        volume: linha.volume,
        projects: linha.projects.size,
        pieces: linha.pieces.size,
      },
    ];
  });
}
